<script setup>
import { ref, onMounted } from 'vue';
import api from '@/services/api.js';
import Swal from 'sweetalert2';
import { useRouter } from 'vue-router';
import logo from "@/assets/img/fondotam-blanco.png";

const router = useRouter();
const activos = ref([]);
const documentoRespaldoGeneral = ref(null);
const cargando = ref(true);

// --- CARGAR MIS ACTIVOS ---
const cargarMisActivos = async () => {
  cargando.value = true;
  try {
    const response = await api.get('mis-activos/');
    if (Array.isArray(response.data)) {
      activos.value = response.data;
      documentoRespaldoGeneral.value = null;
    } else {
      activos.value = response.data.activos || [];
      documentoRespaldoGeneral.value = response.data.documento_respaldo_general || null;
    }
  } catch (error) {
    console.error("Error al cargar los activos:", error);
    Swal.fire('Error', 'No se pudo cargar su inventario asignado.', 'error');
  } finally {
    cargando.value = false;
  }
};

onMounted(() => {
  cargarMisActivos();
});

// --- ACCIONES ---
const reportarFalla = (activo) => {
  Swal.fire({
    title: '¿Reportar Falla?',
    text: `¿Desea solicitar soporte técnico para el equipo: ${activo.codigo_tamep}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#ffc107',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sí, ir al formulario',
    cancelButtonText: 'Cancelar'
  }).then((result) => {
    if (result.isConfirmed) {
      router.push('/solicitud-usuario'); 
    }
  });
};

const verDetalles = (activo) => {
  router.push(`/activo/detalle/${activo.id}`);
};
</script>

<template>
  <div class="container-fluid py-4 min-vh-100">
    <div class="card card-custom shadow-lg border-0 border-radius-xl">
      
      <div class="header-tamep d-flex justify-content-between align-items-center px-4 py-3 border-radius-top">
        <div class="d-flex align-items-center">
          <div class="logo-container-left me-3"><img :src="logo" class="logo-img"></div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Mi Inventario Asignado</h6>
            <small class="text-white opacity-7">Equipos y bienes bajo su responsabilidad legal</small>
          </div>
        </div>
      </div>

      <div class="card-body p-4 bg-section-custom border-radius-bottom">

        <!-- TARJETA DEL RESPALDO GENERAL DEL USUARIO -->
        <div v-if="!cargando" class="row mb-4">
          <div class="col-12">
            <div class="card border border-custom shadow-sm bg-white p-3 border-radius-lg">
              <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                <div class="d-flex align-items-center">
                  <div class="p-3 bg-gradient-danger text-white rounded-circle me-3 shadow-sm d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
                    <i class="fas fa-file-pdf fa-lg"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold text-title-custom">Documento de Respaldo General de Custodia</h6>
                    <p class="text-xs text-custom-muted mb-0">Acta oficial suscrita con la institución que ampara la custodia de sus activos asignados.</p>
                  </div>
                </div>

                <div v-if="documentoRespaldoGeneral">
                  <a :href="documentoRespaldoGeneral" target="_blank" rel="noopener noreferrer" class="btn btn-danger btn-sm mb-0 px-4 shadow-sm text-white">
                    <i class="fas fa-file-download me-2"></i> Ver / Descargar Documento (PDF)
                  </a>
                </div>
                <div v-else>
                  <span class="badge bg-light text-secondary border px-3 py-2 text-xs font-weight-bold">
                    <i class="fas fa-info-circle me-1"></i> Respaldo Pendiente de Carga
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="cargando" class="row justify-content-center py-5">
          <div class="col-12 text-center">
            <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;"></div>
            <h6 class="mt-3 text-custom-muted">Buscando sus equipos en la base de datos...</h6>
          </div>
        </div>

        <div v-else-if="activos.length === 0" class="row justify-content-center py-5">
          <div class="col-md-6 text-center">
            <div class="card shadow-none border border-custom bg-transparent card-empty-custom">
              <div class="card-body py-5">
                <i class="fas fa-box-open fa-4x text-custom-muted mb-3 opacity-5"></i>
                <h5 class="text-title-custom font-weight-bold">No tiene activos asignados</h5>
                <p class="text-custom-muted text-sm">
                  Actualmente no figura ningún equipo bajo su responsabilidad en el sistema. 
                  Si cree que esto es un error, contacte al Jefe Regional de Activos.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="row">
          <div class="col-xl-3 col-lg-4 col-md-6 mb-4" v-for="activo in activos" :key="activo.id">
            <div class="card h-100 shadow-sm hover-card border-0 item-card-custom">
              
              <div class="card-header p-0 mx-3 mt-3 position-relative z-index-1 text-center img-container-custom border-radius-lg">
                <img v-if="activo.imagen_activo" :src="activo.imagen_activo" class="img-fluid border-radius-lg p-2" alt="Foto del activo">
                <i v-else class="fas fa-image fa-3x text-custom-muted opacity-3"></i>
              </div>

              <div class="card-body pt-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="badge text-xs" 
                    :class="activo.estado_actual === 'Bueno' ? 'bg-gradient-success' : (activo.estado_actual === 'Regular' ? 'bg-gradient-warning' : 'bg-gradient-danger')">
                    {{ activo.estado_actual }}
                  </span>
                  <span class="text-xs text-custom-muted font-weight-bold">
                    <i class="fas fa-barcode me-1"></i> {{ activo.codigo_tamep }}
                  </span>
                </div>
                
                <a href="javascript:;" @click="verDetalles(activo)" class="card-title h6 d-block text-title-custom font-weight-bolder mb-1">
                  {{ activo.nombre_especifico }}
                </a>
                
                <p class="text-xs text-custom-muted mb-3">
                  <strong>S/N:</strong> {{ activo.serie || 'No registrada' }}
                </p>
              </div>

              <div class="card-footer card-footer-custom pt-0 px-3 pb-3 d-flex justify-content-between gap-2">
                <button @click="verDetalles(activo)" class="btn btn-sm btn-outline-custom mb-0 flex-grow-1" title="Ver Ficha Técnica">
                  <i class="fas fa-eye me-1"></i> Detalles
                </button>
                <button @click="reportarFalla(activo)" class="btn btn-sm btn-warning mb-0 flex-grow-1 text-white shadow-sm" title="Solicitar Mantenimiento">
                  <i class="fas fa-tools me-1"></i> Soporte
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Transiciones Generales */
.app-transition { transition: all 0.3s ease; }

/* Efecto Hover para las Tarjetas */
.hover-card { transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
.hover-card:hover { transform: translateY(-5px); box-shadow: 0 12px 25px rgba(0,0,0,0.1) !important; }

/* Variables Claras y TAMEP Styles */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }
.border-radius-bottom { border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; }
.logo-container-left { background: white; padding: 5px 12px; border-radius: 8px; }
.logo-img { max-height: 35px; }

.bg-section-custom { background-color: #f8f9fa; }
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, border-color 0.3s ease; border: 1px solid transparent; }
.item-card-custom { background-color: #ffffff; border: 1px solid #e9ecef; }
.img-container-custom { background-color: #f8f9fa; height: 180px; display: flex; align-items: center; justify-content: center; overflow: hidden; transition: background-color 0.3s ease; }
.card-footer-custom { background-color: transparent; border-top: none; }

.text-title-custom { color: #344767; transition: color 0.3s ease; }
.text-custom-muted { color: #6c757d; transition: color 0.3s ease; }
.border-custom { border-color: #dee2e6 !important; transition: border-color 0.3s ease; }

.btn-outline-custom { color: #6c757d; border-color: #ced4da; transition: all 0.3s ease; background: transparent; }
.btn-outline-custom:hover { background-color: #f8f9fa; color: #344767; }

/* Imagen sin deformarse */
.img-fluid { max-width: 100%; max-height: 100%; object-fit: contain; }
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom { background-color: #1e1e1e !important; border: 1px solid #2d2d2d !important; box-shadow: 0 4px 6px rgba(0,0,0,0.3) !important; }
.dark-version .bg-section-custom, .dark-mode .bg-section-custom { background-color: #121212 !important; border: 1px solid #333 !important; }
.dark-version .item-card-custom, .dark-mode .item-card-custom { background-color: #1e1e1e !important; border-color: #333 !important; }

.dark-version .hover-card:hover, .dark-mode .hover-card:hover { box-shadow: 0 12px 25px rgba(0,0,0,0.6) !important; border-color: #4dabf7 !important; }

.dark-version .img-container-custom, .dark-mode .img-container-custom { background-color: #2c2c2c !important; }
.dark-version .card-empty-custom, .dark-mode .card-empty-custom { border-color: #333 !important; }

.dark-version .text-title-custom, .dark-mode .text-title-custom { color: #ffffff !important; }
.dark-version .text-custom-muted, .dark-mode .text-custom-muted { color: #aaaaaa !important; }
.dark-version .border-custom, .dark-mode .border-custom { border-color: #333333 !important; }

/* Botones y Badges en Oscuro */
.dark-version .btn-outline-custom, .dark-mode .btn-outline-custom { color: #e0e0e0 !important; border-color: #555 !important; }
.dark-version .btn-outline-custom:hover, .dark-mode .btn-outline-custom:hover { background-color: #333 !important; color: #fff !important; border-color: #777 !important; }
.dark-version .bg-gradient-success, .dark-mode .bg-gradient-success { background: linear-gradient(135deg, #18ce0f 0%, #15b70e 100%) !important; }
</style>